import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider,
  Alert,
  CircularProgress,
  Tab,
  Tabs,
  IconButton,
  Tooltip,
  LinearProgress
} from '@mui/material';
import {
  Warning,
  AddCircle,
  Message,
  AttachFile,
  Info,
  CheckCircle,
  Gavel,
  Upload
} from '@mui/icons-material';
import DialogCloseButton from '../common/DialogCloseButton';

/**
 * Client Dispute Manager
 * Allows clients to raise disputes, add evidence, send messages, and track status
 */
const ClientDisputeManager = ({ apiClient }) => {
  const [myDisputes, setMyDisputes] = useState([]);
  const [selectedDispute, setSelectedDispute] = useState(null);
  const [loading, setLoading] = useState(false);
  const [raiseDisputeOpen, setRaiseDisputeOpen] = useState(false);
  const [messagesOpen, setMessagesOpen] = useState(false);
  const [evidenceOpen, setEvidenceOpen] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);

  // Raise dispute form
  const [disputeForm, setDisputeForm] = useState({
    escrowId: '',
    milestoneId: '',
    reason: '',
    description: ''
  });

  // Evidence form
  const [evidenceForm, setEvidenceForm] = useState({
    type: 'photo',
    description: '',
    file: null
  });
  const [uploadProgress, setUploadProgress] = useState(0);

  // Messages
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');

  // Available escrows/milestones
  const [availableEscrows, setAvailableEscrows] = useState([]);
  const [availableMilestones, setAvailableMilestones] = useState([]);

  useEffect(() => {
    loadMyDisputes();
    loadAvailableEscrows();
  }, []);

  useEffect(() => {
    if (disputeForm.escrowId) {
      loadMilestonesForEscrow(disputeForm.escrowId);
    }
  }, [disputeForm.escrowId]);

  const loadMyDisputes = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/escrow/my-disputes');
      if (response.data.success) {
        setMyDisputes(response.data.data);
      }
    } catch (error) {
      console.error('Failed to load disputes:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableEscrows = async () => {
    try {
      const response = await apiClient.get('/escrow/my-escrows');
      if (response.data.success) {
        setAvailableEscrows(response.data.data);
      }
    } catch (error) {
      console.error('Failed to load escrows:', error);
    }
  };

  const loadMilestonesForEscrow = async (escrowId) => {
    try {
      const response = await apiClient.get(`/escrow/${escrowId}/milestones`);
      if (response.data.success) {
        setAvailableMilestones(response.data.data);
      }
    } catch (error) {
      console.error('Failed to load milestones:', error);
    }
  };

  const handleRaiseDispute = async () => {
    try {
      const response = await apiClient.post(`/escrow/${disputeForm.escrowId}/dispute`, {
        milestoneId: disputeForm.milestoneId,
        reason: disputeForm.reason,
        description: disputeForm.description
      });

      if (response.data.success) {
        alert('Dispute raised successfully. Our team will review it shortly.');
        setRaiseDisputeOpen(false);
        setDisputeForm({ escrowId: '', milestoneId: '', reason: '', description: '' });
        loadMyDisputes();
      }
    } catch (error) {
      alert('Failed to raise dispute: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleAddEvidence = async () => {
    if (!selectedDispute || !evidenceForm.file) return;

    try {
      // Upload file first (you'll need to implement file upload endpoint)
      const formData = new FormData();
      formData.append('file', evidenceForm.file);
      formData.append('disputeId', selectedDispute.id);

      // Simulated upload - replace with actual upload endpoint
      const uploadResponse = await apiClient.post('/uploads/evidence', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        }
      });

      const fileUrl = uploadResponse.data.url;

      // Add evidence to dispute
      await apiClient.post(`/disputes/${selectedDispute.id}/evidence`, {
        evidenceType: evidenceForm.type,
        fileUrl: fileUrl,
        description: evidenceForm.description
      });

      alert('Evidence added successfully');
      setEvidenceOpen(false);
      setEvidenceForm({ type: 'photo', description: '', file: null });
      setUploadProgress(0);
    } catch (error) {
      alert('Failed to add evidence: ' + error.message);
    }
  };

  const loadMessages = async (disputeId) => {
    try {
      const response = await apiClient.get(`/disputes/${disputeId}/messages`);
      if (response.data.success) {
        setMessages(response.data.data);
      }
    } catch (error) {
      console.error('Failed to load messages:', error);
    }
  };

  const handleSendMessage = async () => {
    if (!selectedDispute || !newMessage.trim()) return;

    try {
      await apiClient.post(`/disputes/${selectedDispute.id}/messages`, {
        message: newMessage,
        messageType: 'message'
      });

      setNewMessage('');
      loadMessages(selectedDispute.id);
    } catch (error) {
      alert('Failed to send message: ' + error.message);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      open: 'warning',
      investigating: 'info',
      escalated: 'error',
      mediation: 'secondary',
      resolved: 'success',
      closed: 'default'
    };
    return colors[status] || 'default';
  };

  const getSeverityColor = (severity) => {
    const colors = {
      low: 'success',
      medium: 'warning',
      high: 'error',
      critical: 'error'
    };
    return colors[severity] || 'default';
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString();
  };

  const renderDisputesList = () => (
    <Grid container spacing={3}>
      {myDisputes.length === 0 ? (
        <Grid item xs={12}>
          <Alert severity="info">
            You have no active disputes. If you experience issues with a project, you can raise a dispute.
          </Alert>
        </Grid>
      ) : (
        myDisputes.map((dispute) => (
          <Grid item xs={12} md={6} key={dispute.id}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6">
                    {dispute.project_name || `Project ${dispute.project_id?.substring(0, 8)}`}
                  </Typography>
                  <Chip 
                    label={dispute.status}
                    color={getStatusColor(dispute.status)}
                    size="small"
                  />
                </Box>

                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Reason: <strong>{dispute.dispute_reason?.replace('_', ' ')}</strong>
                </Typography>

                <Typography variant="body2" paragraph>
                  {dispute.description}
                </Typography>

                <Box sx={{ mb: 2 }}>
                  {dispute.severity_level && (
                    <Chip 
                      label={`Severity: ${dispute.severity_level}`}
                      color={getSeverityColor(dispute.severity_level)}
                      size="small"
                      sx={{ mr: 1 }}
                    />
                  )}
                  {dispute.fraud_score >= 30 && (
                    <Chip 
                      label={`Fraud Alert: ${dispute.fraud_score}%`}
                      color="error"
                      size="small"
                      icon={<Warning />}
                    />
                  )}
                </Box>

                <Typography variant="caption" display="block" gutterBottom>
                  Created: {formatDate(dispute.created_at)}
                </Typography>

                {dispute.resolution && (
                  <Alert severity="success" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      <strong>Resolution:</strong> {dispute.resolution}
                    </Typography>
                  </Alert>
                )}

                <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    startIcon={<Message />}
                    onClick={() => {
                      setSelectedDispute(dispute);
                      loadMessages(dispute.id);
                      setMessagesOpen(true);
                    }}
                  >
                    Messages
                  </Button>
                  <Button
                    size="small"
                    startIcon={<AttachFile />}
                    onClick={() => {
                      setSelectedDispute(dispute);
                      setEvidenceOpen(true);
                    }}
                    disabled={dispute.status === 'resolved' || dispute.status === 'closed'}
                  >
                    Add Evidence
                  </Button>
                  <Button
                    size="small"
                    startIcon={<Info />}
                    onClick={() => setSelectedDispute(dispute)}
                  >
                    Details
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))
      )}
    </Grid>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">
          <Gavel sx={{ mr: 1, verticalAlign: 'middle' }} />
          My Disputes
        </Typography>
        <Button
          variant="contained"
          color="error"
          startIcon={<Warning />}
          onClick={() => setRaiseDisputeOpen(true)}
        >
          Raise Dispute
        </Button>
      </Box>

      <Tabs value={currentTab} onChange={(e, v) => setCurrentTab(v)} sx={{ mb: 3 }}>
        <Tab label="Active Disputes" />
        <Tab label="Resolved Disputes" />
      </Tabs>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        renderDisputesList()
      )}

      {/* Raise Dispute Dialog */}
      <Dialog open={raiseDisputeOpen} onClose={() => setRaiseDisputeOpen(false)} maxWidth="md" fullWidth>
        <DialogCloseButton onClick={() => setRaiseDisputeOpen(false)} />
        <DialogTitle>
          <Warning sx={{ color: 'error.main', mr: 1, verticalAlign: 'middle' }} />
          Raise a Dispute
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 3 }}>
            Please provide detailed information about the issue. Our dispute resolution team will review your case and contact both parties.
          </Alert>

          <TextField
            fullWidth
            select
            label="Select Project/Escrow"
            value={disputeForm.escrowId}
            onChange={(e) => setDisputeForm({ ...disputeForm, escrowId: e.target.value })}
            sx={{ mb: 2 }}
          >
            {availableEscrows.map((escrow) => (
              <MenuItem key={escrow.id} value={escrow.id}>
                {escrow.project_name || escrow.project_id} - ${escrow.total_amount}
              </MenuItem>
            ))}
          </TextField>

          {disputeForm.escrowId && (
            <TextField
              fullWidth
              select
              label="Select Milestone (optional)"
              value={disputeForm.milestoneId}
              onChange={(e) => setDisputeForm({ ...disputeForm, milestoneId: e.target.value })}
              sx={{ mb: 2 }}
            >
              <MenuItem value="">General Dispute (all milestones)</MenuItem>
              {availableMilestones.map((milestone) => (
                <MenuItem key={milestone.id} value={milestone.id}>
                  {milestone.description} - ${milestone.amount}
                </MenuItem>
              ))}
            </TextField>
          )}

          <TextField
            fullWidth
            select
            label="Dispute Reason"
            value={disputeForm.reason}
            onChange={(e) => setDisputeForm({ ...disputeForm, reason: e.target.value })}
            sx={{ mb: 2 }}
            required
          >
            <MenuItem value="work_not_completed">Work Not Completed</MenuItem>
            <MenuItem value="poor_quality">Poor Quality Work</MenuItem>
            <MenuItem value="timeline_breach">Timeline Breach</MenuItem>
            <MenuItem value="scope_change">Scope Change Issues</MenuItem>
            <MenuItem value="payment_issue">Payment Issue</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </TextField>

          <TextField
            fullWidth
            multiline
            rows={4}
            label="Describe the Issue"
            value={disputeForm.description}
            onChange={(e) => setDisputeForm({ ...disputeForm, description: e.target.value })}
            placeholder="Please provide as much detail as possible about the issue..."
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRaiseDisputeOpen(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            color="error" 
            onClick={handleRaiseDispute}
            disabled={!disputeForm.escrowId || !disputeForm.reason || !disputeForm.description}
          >
            Submit Dispute
          </Button>
        </DialogActions>
      </Dialog>

      {/* Messages Dialog */}
      <Dialog open={messagesOpen} onClose={() => setMessagesOpen(false)} maxWidth="md" fullWidth>
        <DialogCloseButton onClick={() => setMessagesOpen(false)} />
        <DialogTitle>Dispute Messages</DialogTitle>
        <DialogContent>
          <List sx={{ maxHeight: 400, overflow: 'auto', mb: 2 }}>
            {messages.length === 0 ? (
              <Alert severity="info">No messages yet. Start the conversation!</Alert>
            ) : (
              messages.map((msg, index) => (
                <React.Fragment key={msg.id}>
                  <ListItem alignItems="flex-start">
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="subtitle2">{msg.sender_name}</Typography>
                          <Typography variant="caption" color="textSecondary">
                            {formatDate(msg.created_at)}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          {msg.message}
                        </Typography>
                      }
                    />
                  </ListItem>
                  {index < messages.length - 1 && <Divider />}
                </React.Fragment>
              ))
            )}
          </List>

          <TextField
            fullWidth
            multiline
            rows={3}
            label="Your Message"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type your message here..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMessagesOpen(false)}>Close</Button>
          <Button 
            variant="contained" 
            onClick={handleSendMessage}
            disabled={!newMessage.trim()}
            startIcon={<Message />}
          >
            Send Message
          </Button>
        </DialogActions>
      </Dialog>

      {/* Evidence Upload Dialog */}
      <Dialog open={evidenceOpen} onClose={() => setEvidenceOpen(false)} maxWidth="sm" fullWidth>
        <DialogCloseButton onClick={() => setEvidenceOpen(false)} />
        <DialogTitle>Add Evidence</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              select
              label="Evidence Type"
              value={evidenceForm.type}
              onChange={(e) => setEvidenceForm({ ...evidenceForm, type: e.target.value })}
              sx={{ mb: 2 }}
            >
              <MenuItem value="photo">Photo</MenuItem>
              <MenuItem value="video">Video</MenuItem>
              <MenuItem value="document">Document</MenuItem>
              <MenuItem value="screenshot">Screenshot</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </TextField>

            <TextField
              fullWidth
              label="Description"
              multiline
              rows={2}
              value={evidenceForm.description}
              onChange={(e) => setEvidenceForm({ ...evidenceForm, description: e.target.value })}
              sx={{ mb: 2 }}
              placeholder="Describe this evidence..."
            />

            <Button
              variant="outlined"
              component="label"
              fullWidth
              startIcon={<Upload />}
              sx={{ mb: 2 }}
            >
              {evidenceForm.file ? evidenceForm.file.name : 'Choose File'}
              <input
                type="file"
                hidden
                onChange={(e) => setEvidenceForm({ ...evidenceForm, file: e.target.files[0] })}
                accept="image/*,video/*,.pdf,.doc,.docx"
              />
            </Button>

            {uploadProgress > 0 && uploadProgress < 100 && (
              <Box sx={{ mb: 2 }}>
                <LinearProgress variant="determinate" value={uploadProgress} />
                <Typography variant="caption" align="center" display="block">
                  Uploading... {uploadProgress}%
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEvidenceOpen(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleAddEvidence}
            disabled={!evidenceForm.file}
            startIcon={<AttachFile />}
          >
            Add Evidence
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientDisputeManager;

